import {
  CopyIcon,
  Crosshair1Icon,
  ListBulletIcon,
  ResetIcon,
  TrashIcon,
  VideoIcon,
} from '@radix-ui/react-icons';
import { Flex, ScrollArea } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { BaseballIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PitchCard } from 'components/sections/pitch-list/pitch-card';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import {
  ContextMenuContext,
  ContextMenuProvider,
} from 'contexts/context-menu.context';
import { MachineContext } from 'contexts/machine.context';
import { ALLOW_CLOSED_LOOP_TRAINING } from 'enums/machine.enums';
import { PitchListDialog } from 'enums/pitch-lists';
import { useOnContextMenu } from 'hooks/useOnContextMenu';
import { t } from 'i18next';
import { TrainPitchesFn } from 'interfaces/function-types';
import { IVerboseComponent } from 'interfaces/i-components';
import { ITableAction } from 'interfaces/tables/action';
import { PLStoreMode } from 'lib_ts/enums/pitch-list.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { paths } from 'routes/paths';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

// group name starts with _ so that it renders as a separator in the menu
enum ActionGroup {
  Primary = '_1',
  Secondary = '_2',
  Tertiary = '_3',
}

// e.g. start loading pitches 900px before they come into view
const OVERSCAN_PIXELS = 900;

interface IProps {
  onTrainFn: TrainPitchesFn;
}

export const PitchListBodyHoC = (props: IProps) => {
  const navigate = useNavigate();
  const isInGame = useAuthStore((s) => s.isInGame);

  const readyToTrain = useTrainedMsStore((s) => s.readyToTrain);

  const { machine } = useContext(MachineContext);

  const mode = usePitchListStore((s) => s.mode);

  const activeReadOnly = useListLibraryStore((s) => s.isActiveListReadOnly());

  const targetActions = useMemo(() => {
    const isUntrained = (p: IPitch) => !p._mx_tms?._mx_trained;

    const output: ITableAction[] = [
      {
        // * LIBRARY-ONLY > open the specific PL
        group: ActionGroup.Primary,
        prefixIcon: <ListBulletIcon />,
        label: 'pl.open-pitch-list',
        // hidden for list view because it would just re-open the same list
        invisibleFn: () => mode !== PLStoreMode.Library,
        onClick: (p: IPitch) => {
          navigate(paths.pitches.list(p._parent_id));
        },
      },
      {
        group: ActionGroup.Primary,
        prefixIcon: <BaseballIcon />,
        label: 'common.pitch-details',
        onClick: (p: IPitch) =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.Data,
            pitches: [p],
          }),
      },
      {
        group: ActionGroup.Primary,
        prefixIcon: <Crosshair1Icon />,
        label: 'common.train-pitch',
        disableFn: () => !readyToTrain,
        invisibleFn: (p: IPitch) =>
          !isUntrained(p) || // no need to train if trained
          !!p.debugMs, // can't train debug pitches
        onClick: (p: IPitch) => props.onTrainFn([p._id]),
      },
      {
        group: ActionGroup.Primary,
        prefixIcon: <Crosshair1Icon />,
        label: 'common.continue-training',
        disableFn: () => !readyToTrain,
        invisibleFn: (p: IPitch) =>
          isUntrained(p) || // no need to continue if not trained
          !!p.debugMs, // can't train debug pitches
        onClick: (p: IPitch) => props.onTrainFn([p._id], true),
      },
      {
        group: ActionGroup.Primary,
        prefixIcon: <VideoIcon />,
        label: 'pl.change-video',
        invisibleFn: () => activeReadOnly,
        onClick: (p: IPitch) =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.ChangeVideo,
            pitches: [p],
          }),
      },
      {
        group: ActionGroup.Primary,
        prefixIcon: <CopyIcon />,
        label: t('common.duplicate-x', { x: t('common.pitch') }),
        invisibleFn: () => isInGame,
        onClick: (p: IPitch) =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.Copy,
            pitches: [p],
          }),
      },
      {
        // ! deprecate
        group: ActionGroup.Secondary,
        label: 'pl.optimize-pitch',
        invisibleFn: (p: IPitch) =>
          isUntrained(p) || !ALLOW_CLOSED_LOOP_TRAINING,
        onClick: (p: IPitch) =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.Optimize,
            pitches: [p],
          }),
      },
      {
        // ! deprecate
        group: ActionGroup.Secondary,
        label: 'pl.edit-breaks',
        invisibleFn: (p: IPitch) =>
          isUntrained(p) || !ALLOW_CLOSED_LOOP_TRAINING,
        onClick: (p: IPitch) =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.EditBreaks,
            pitches: [p],
          }),
      },
      {
        // ! deprecate
        group: ActionGroup.Secondary,
        label: 'pl.edit-spins',
        invisibleFn: (p: IPitch) =>
          isUntrained(p) || !ALLOW_CLOSED_LOOP_TRAINING,
        onClick: (p: IPitch) =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.EditSpins,
            pitches: [p],
          }),
      },
      {
        group: ActionGroup.Tertiary,
        prefixIcon: <ResetIcon />,
        label: 'common.reset-training-data',
        disableFn: () => !readyToTrain, // prevent resetting training data if not ready to train, in case of accidental reset w/o a way to undo
        invisibleFn: (p: IPitch) =>
          activeReadOnly || // can't reset read-only pitches
          isUntrained(p) || // can't reset untrained pitches
          !!p.debugMs, // can't reset debug pitches
        onClick: (p: IPitch) =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.Reset,
            pitches: [p],
          }),
        color: RADIX.COLOR.DANGER,
      },
      {
        group: ActionGroup.Tertiary,
        prefixIcon: <TrashIcon />,
        label: t('common.delete-x', { x: t('common.pitch') }),
        invisibleFn: () => activeReadOnly || isInGame,
        onClick: (p: IPitch) =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.DeletePitches,
            pitches: [p],
          }),
        color: RADIX.COLOR.DANGER,
      },
    ];

    return output;
  }, [
    activeReadOnly,
    readyToTrain,
    machine.last_hardware_changed,
    mode,
    props.onTrainFn,
    isInGame,
  ]);

  return (
    <ErrorBoundary identifier="PitchListBodyHoC">
      <ScrollArea className="PitchListBodyScrollArea" scrollbars="vertical">
        <ContextMenuProvider
          identifier="PLBodyContextMenu"
          targetActions={targetActions}
        >
          <PitchListBody
            identifier="PitchListBody"
            targetActions={targetActions}
            onTrainFn={props.onTrainFn}
          />
        </ContextMenuProvider>
      </ScrollArea>
    </ErrorBoundary>
  );
};

const PitchListBody = (
  props: IVerboseComponent &
    IProps & {
      targetActions: ITableAction[];
    }
) => {
  const { setTarget } = useContext(ContextMenuContext);
  const filteredPitches = usePitchListStore((s) => s.filteredPitches);

  const { onContextMenuHandlers } = useOnContextMenu({
    longPressEnabled: true,
    onContextMenu: () => {
      verboseDebug(props, 'context menu target cleared');
      setTarget(undefined);
    },
  });

  return (
    <Flex direction="column" height="100%" {...onContextMenuHandlers}>
      <Virtuoso
        data={filteredPitches}
        totalCount={filteredPitches.length}
        overscan={OVERSCAN_PIXELS}
        itemContent={(_, p) => (
          <PitchCard
            key={p._id}
            pitch={p}
            targetActions={props.targetActions}
            onTrainFn={props.onTrainFn}
          />
        )}
      />
    </Flex>
  );
};
